import { z } from 'zod';

import { MediaSchema } from '@/shared/lib/media';
import { MoneySchema } from '@/shared/lib/money';

export const DeliveryMethodIdSchema = z.enum(['pickup', 'courier', 'post']);
export type DeliveryMethodId = z.infer<typeof DeliveryMethodIdSchema>;

export const PaymentMethodIdSchema = z.enum(['card', 'sbp', 'parts', 'cod']);
export type PaymentMethodId = z.infer<typeof PaymentMethodIdSchema>;

export const OrderStatusSchema = z.enum([
  'awaiting_payment',
  'payment_failed',
  'confirmed',
  'paid',
  'preparing',
  'shipped',
  'delivered',
  'cancelled',
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderItemSchema = z.object({
  sku: z.string(),
  productSlug: z.string(),
  productKind: z.enum(['item', 'bundle']),
  name: z.string(),
  media: MediaSchema.nullable(),
  tint: z.string().nullable(),
  variantLabel: z.string(),
  quantity: z.number().int().min(1),
  unitPrice: MoneySchema,
  lineTotal: MoneySchema,
  preorder: z.boolean(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const OrderSchema = z.object({
  id: z.string(),
  number: z.string(),
  createdAt: z.string(),
  status: OrderStatusSchema,
  items: z.array(OrderItemSchema).min(1),
  itemsCount: z.number().int().min(1),
  recipient: z.object({ name: z.string(), phone: z.string(), email: z.string() }),
  delivery: z.object({
    method: DeliveryMethodIdSchema,
    methodLabel: z.string(),
    city: z.string(),
    address: z.string(),
    price: MoneySchema,
  }),
  payment: z.object({
    method: PaymentMethodIdSchema,
    methodLabel: z.string(),
    status: z.enum(['pending', 'paid', 'failed', 'on_delivery']),
    receipt: z
      .object({ number: z.string(), fiscalSign: z.string(), issuedAt: z.string() })
      .nullable(),
  }),
  comment: z.string().nullable(),
  subtotal: MoneySchema,
  discountTotal: MoneySchema,
  total: MoneySchema,
  bonusAccrual: z.number().int().min(0),
  hasPreorder: z.boolean(),
});
export type Order = z.infer<typeof OrderSchema>;
export const OrderListSchema = z.array(OrderSchema);

export const PlaceOrderSchema = z
  .object({
    recipient: z
      .object({
        name: z.string().trim().min(2, 'Укажите имя').max(80),
        phone: z.string().regex(/^9\d{9}$/, 'Проверьте номер телефона'),
        email: z.email('Проверьте e-mail').max(120),
      })
      .strict(),
    delivery: z
      .object({
        city: z.string().trim().min(2, 'Укажите город').max(80),
        method: DeliveryMethodIdSchema,
        pickupPointId: z.string().max(64).nullable(),
        address: z.string().trim().max(200).nullable(),
      })
      .strict(),
    comment: z.string().trim().max(500),
    paymentMethod: PaymentMethodIdSchema,
  })
  .strict();
export type PlaceOrderCommand = z.infer<typeof PlaceOrderSchema>;

export const PlaceOrderResultSchema = z.object({
  orderId: z.string(),
  number: z.string(),
  /** Where the browser goes next: payment page or order status. */
  next: z.enum(['payment', 'order']),
});
export type PlaceOrderResult = z.infer<typeof PlaceOrderResultSchema>;

export const MockPaymentSchema = z.object({ outcome: z.enum(['success', 'fail']) }).strict();

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  awaiting_payment: 'Ожидает оплаты',
  payment_failed: 'Оплата не прошла',
  confirmed: 'Принят · оплата при получении',
  paid: 'Заказ оплачен',
  preparing: 'Готовится к отправке',
  shipped: 'Передан в доставку',
  delivered: 'Получен',
  cancelled: 'Отменён',
};

export const ORDER_STAGES = [
  'Заказ оплачен',
  'Готовится к отправке',
  'Передан в доставку',
  'Получен',
] as const;

/** Index of the current stage in ORDER_STAGES, or -1 before payment. */
export function orderStageIndex(status: OrderStatus): number {
  switch (status) {
    case 'paid':
    case 'confirmed':
      return 0;
    case 'preparing':
      return 1;
    case 'shipped':
      return 2;
    case 'delivered':
      return 3;
    default:
      return -1;
  }
}

export function isActiveOrder(status: OrderStatus): boolean {
  return status !== 'delivered' && status !== 'cancelled';
}
